import {
  IconBell,
  IconBinocularsGray,
  IconChainLink,
  IconChartBarGray,
  IconChartRadius,
  IconClockGray,
  IconCloudCheckGray,
  IconDashboard,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconSecurity,
  IconShareGray,
  IconTagGray,
  IconTvocGray,
  IconWarning,
  IconZoneGray,
} from '@assets/images/icon';
import { rtlsSystem } from '@assets/images/solution';
import mainVideo from '@src/assets/images/solution/constructionSafety/video/rtlsVideo.mp4';

import image1 from '@src/assets/images/solution/constructionSop/img/1.avif';
import image2 from '@src/assets/images/solution/constructionSop/img/2.avif';
import image3 from '@src/assets/images/solution/constructionSop/img/3.avif';
import image4 from '@src/assets/images/solution/constructionSop/img/4.avif';
import image5 from '@src/assets/images/solution/constructionSop/img/5.avif';
import image6 from '@src/assets/images/solution/constructionSop/img/6.avif';
import image7 from '@src/assets/images/solution/constructionSop/img/7.avif';
import image8 from '@src/assets/images/solution/constructionSop/img/8.avif';

export default {
  type: 'Construction Solution',

  banner: {
    title: 'Automatic Hazard Detection and Response',
    desc: 'Detect on-site hazards in real time according to SOPs and respond automatically. All records are stored as data and can be used as evidence for audits and reporting. Digital technology streamlines safety management and prevents accidents in advance.',
  },

  video: {
    title: 'Automatically detect and quickly respond to incidents based on standardized procedures',
    desc: 'Detect worker movements and risk factors in real time, and automatically link alerts, logging, and response based on SOPs for systematic safety actions. Data is automatically saved for future analysis and audits.',
    play: mainVideo,
  },

  func: [
    {
      theme: 'Automatic Hazard Detection and Real-time Response',
      title: 'SOP-based Automatic Hazard Recognition',
      desc: 'Analyze worker behavior and surroundings to automatically detect tasks deviating from SOPs and use AI to identify abnormal signs.',
      sub1: 'High accuracy',
      icon1: IconRulerGray,
      sub2: 'Real-time status',
      icon2: IconClockGray,
      image: image1,
    },
    {
      title: 'Auto-call SOP Response Panel on Hazard Detection',
      desc: 'When a hazard event occurs, the SOP response procedure for that task type automatically pops up. Responders can start the process immediately with one click, helping secure the golden time.',
      sub1: 'Fast data integration',
      icon1: IconTvocGray,
      sub2: 'Real-time location tracking',
      icon2: IconBinocularsGray,
      image: image2,
    },
    {
      title: 'Automatic CCTV Call and View Log Storage',
      desc: 'Automatically calls CCTV footage of the hazard area, and logs all viewing history and times. This can be used directly for incident investigation or legal evidence later.',
      sub1: 'Real-time location tracking',
      icon1: IconBinocularsGray,
      sub2: 'High accuracy',
      icon2: IconRulerGray,
      image: image3,
    },
    {
      title: 'Mobile SOP Response Participation',
      desc: 'Even without a PC, managers or supervisors on the move can participate in SOP response via smartphone, ensuring real-time action. Urgent situations can be handled without spatial constraints.',
      sub1: 'Flexible area setting',
      icon1: IconZoneGray,
      sub2: 'Multiple area settings',
      icon2: IconTagGray,
      image: image4,
    },
    {
      theme: 'SOP Operation and Custom Management',
      title: 'SOP Response Status Visualization',
      desc: 'Displays the current step and completion status for each ongoing SOP scenario on the dashboard, giving a clear overview of the entire response flow. Managers can allocate extra staff or resources based on priority.',
      sub1: 'Flexible area setting',
      icon1: IconTagGray,
      sub2: 'Multiple area settings',
      icon2: IconZoneGray,
      image: image5,
    },
    {
      title: 'Auto-generate and Download Reports',
      desc: 'Automatically generates reports based on key data such as response logs, alert times, and completion status. Reports can be downloaded in PDF or Excel format, saving time and manpower for weekly/monthly reporting.',
      sub1: 'High accuracy',
      icon1: IconZoneGray,
      sub2: 'Multiple area settings',
      icon2: IconTagGray,
      image: image6,
    },
    {
      title: 'Condition-based Alert Scenario Creation',
      desc: 'Combine conditions like temperature, gas, location, and activity status to create custom alert rules. This allows setting automatic hazard detection triggers linked with various IoT sensors.',
      sub1: 'Quick installation',
      icon1: IconTagGray,
      sub2: 'Multiple area settings',
      icon2: IconTagGray,
      image: image7,
    },
    {
      title: 'Edit SOP Scenarios and Customize for Sites',
      desc: 'Edit or newly configure SOP steps according to site characteristics and task types. With a GUI interface, anyone can easily add or remove steps and apply them on site.',
      sub1: 'Real-time visualization',
      icon1: IconShareGray,
      sub2: 'Multiple area settings',
      icon2: IconTagGray,
      image: image8,
    },
  ],

  subfunc: {
    title: 'Various Features',
    desc: 'Ensuring worker safety increases efficiency and reduces losses due to accidents. ORBRO provides optimal functions to protect construction site workers. The system is designed to respond appropriately when workers approach danger zones, accidents occur, or when heavy machinery poses potential risks.',
    data: [
      { icon: IconWarning, title: 'Real-time Risk Detection', desc: 'Sensors detect risks & notify.' },
      { icon: IconBell, title: 'Auto SOP Execution', desc: 'Risk triggers auto SOP popup.' },
      { icon: IconDashboard, title: 'Dashboard Monitoring', desc: 'Check all safety data easily.' },
      { icon: IconBell, title: 'Auto Alert Sending', desc: 'Send alerts via SMS, email, push.' },
      { icon: IconRoute, title: 'CCTV Auto Link', desc: 'Event calls CCTV & logs view.' },
      { icon: IconSearch, title: 'Mobile Response', desc: 'Run SOP on mobile anytime.' },
      { icon: IconChartRadius, title: 'Live Progress View', desc: 'See SOP steps in real time.' },
      { icon: IconChainLink, title: 'Risk Trigger Setup', desc: 'Set sensor & action triggers.' },
      { icon: IconChartRadius, title: 'Auto Report', desc: 'Make PDF/Excel from logs.' },
      { icon: IconSecurity, title: 'Audit Evidence', desc: 'Auto save logs for audits.' },
    ],
  },

  system: {
    title: 'System Configuration',
    img: rtlsSystem,
  },

  productTitle: 'Product Composition',
  product: [{ tag: 'workerPackage' }, { tag: 'assetPackage' }],

  contents: [{ data: 'constructionAsset' }, { data: 'constructionEquipment' }, { data: 'blogRTLS' }, { data: 'blogMedical' }],
};
